import React from 'react';
import { useSelector } from 'react-redux';
import { getTheme } from '@fluentui/react';
import { RootState } from '../../store/rootReducer';
import { INormalizedProduct } from '../../models/NormalizedProduct';

/**
 * The attributes the active search's boosts actually matched on this product, shown on the
 * result row so the ranking explains itself: searching os=windows surfaces "Operating System:
 * Windows 11 Pro" on each boosted result even when the curated short description omits it.
 */
const BoostedAttributeHighlights: React.FC<{ product: INormalizedProduct }> = (props) => {
    const criteria = useSelector((state: RootState) => state.search.criteria);
    const theme = getTheme();

    const matches = React.useMemo(() => {
        const boosts = criteria.boost ?? [];
        const facets = props.product.facets;
        if (boosts.length === 0 || facets == null) return [];
        const out: { name: string, value: string }[] = [];
        const seen = new Set<string>();
        for (const boost of boosts) {
            const facetKey = Object.keys(facets).find((k) => k.toLowerCase() === boost.name.toLowerCase());
            if (!facetKey || seen.has(facetKey.toLowerCase())) continue;
            const value = facets[facetKey];
            if (typeof value === 'string' && value.toLowerCase() === boost.value.toLowerCase()) {
                seen.add(facetKey.toLowerCase());
                out.push({ name: facetKey, value });
            }
        }
        return out;
    }, [criteria.boost, props.product.facets]);

    if (matches.length === 0) return null;
    return (
        <p style={{ margin: '2px 0 0', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {matches.map((m) => (
                <span
                    key={m.name}
                    style={{
                        background: theme.palette.themeLighterAlt,
                        border: `1px solid ${theme.palette.themeLight}`,
                        borderRadius: 3,
                        padding: '0 6px',
                        fontSize: 11,
                        color: theme.palette.neutralPrimary,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {m.name}: {m.value}
                </span>
            ))}
        </p>
    );
};

export default BoostedAttributeHighlights;
